import { useState, useEffect } from "react";
import "./PaymentPopup.css";

const PaymentPopup = ({ isOpen, onClose, amount, itemDetails, customerDetails, eventId, stallNumber, onPaymentSuccess }) => {
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      // Cleanup if needed
    };
  }, []);

  if (!isOpen) return null;

  const handlePayment = async () => {
    setProcessing(true);
    
    try {
      // Create order on backend
      const orderResponse = await fetch('https://royaleheritagefiesta.com/royaleheritagefiesta/api/payments/create-order.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          currency: 'INR',
          receipt: `stall_${stallNumber}_${Date.now()}`
        })
      });
      
      const orderData = await orderResponse.json();
      
      if (!orderData.success) {
        throw new Error(orderData.error || 'Failed to create order');
      }
      
      const options = {
        key: "rzp_live_SlHZ4BKrM9xQKn", // Your Razorpay key
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Royal Heritage Fiesta",
        description: `Booking for ${itemDetails.name}`,
        image: "https://royaleheritagefiesta.com/logo.png",
        order_id: orderData.orderId,
        handler: function(response) {
          // Payment successful
          verifyPayment(response, orderData.orderId);
        },
        prefill: {
          name: customerDetails.name,
          email: customerDetails.email,
          contact: customerDetails.phone
        },
        notes: {
          eventId: eventId,
          stallNumber: stallNumber,
          eventName: itemDetails.eventName
        },
        theme: {
          color: "#b8860b"
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
            console.log("Payment modal closed");
          }
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (error) {
      console.error("Payment error:", error);
      alert("Failed to initiate payment. Please try again.");
      setProcessing(false);
    }
  };
  
  const verifyPayment = async (paymentResponse, orderId) => {
    try {
      const verifyResponse = await fetch('http://localhost/royaleheritagefiesta/api/payments/verify-payment.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_signature: paymentResponse.razorpay_signature,
          orderId: orderId
        })
      });
      
      const result = await verifyResponse.json();
      
      if (result.success) {
        // Payment verified successfully
        onPaymentSuccess({
          paymentId: paymentResponse.razorpay_payment_id,
          orderId: paymentResponse.razorpay_order_id,
          amount: amount,
          paymentMethod: "RAZORPAY",
          status: "SUCCESS"
        });
        onClose();
      } else {
        throw new Error(result.error || "Payment verification failed");
      }
    } catch (error) {
      console.error("Verification error:", error);
      alert("Payment verification failed. Please contact support.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="payment-popup-overlay" onClick={onClose}>
      <div className="payment-popup" onClick={(e) => e.stopPropagation()}>
        <button className="payment-close-btn" onClick={onClose}>×</button>
        
        <div className="payment-header">
          <h3>💳 Complete Payment</h3>
        </div>

        <div className="payment-body">
          {/* Order Summary */}
          <div className="order-summary">
            <h4>Order Summary</h4>
            <div className="summary-item">
              <span>{itemDetails.name}</span>
              <span>₹{amount}</span>
            </div>
            <div className="summary-item">
              <span>Event: {itemDetails.eventName}</span>
              <span>-</span>
            </div>
            <div className="summary-item">
              <span>Date: {itemDetails.eventDate ? new Date(itemDetails.eventDate).toLocaleDateString() : 'TBA'}</span>
              <span>-</span>
            </div>
            <div className="summary-total">
              <span>Total Amount</span>
              <span>₹{amount}</span>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="payment-methods">
            <h4>Select Payment Method</h4>
            <div className="method-options">
              <label className={`method-option ${paymentMethod === 'card' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span className="method-icon">💳</span>
                <span>Credit/Debit Card</span>
              </label>

              <label className={`method-option ${paymentMethod === 'upi' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="upi"
                  checked={paymentMethod === 'upi'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span className="method-icon">📱</span>
                <span>UPI</span>
              </label>

              <label className={`method-option ${paymentMethod === 'netbanking' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="netbanking"
                  checked={paymentMethod === 'netbanking'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span className="method-icon">🏦</span>
                <span>Net Banking</span>
              </label>
            </div>
          </div>

          {/* Payment Info */}
          <div className="payment-info">
            <div className="info-box">
              <span className="info-icon">🔒</span>
              <span className="info-text">Your payment is secure and encrypted</span>
            </div>
          </div>
        </div>

        <div className="payment-footer">
          <button className="btn-cancel" onClick={onClose} disabled={processing}>
            Cancel
          </button>
          <button 
            className="btn-pay" 
            onClick={handlePayment}
            disabled={processing}
          >
            {processing ? 'Processing...' : `Pay ₹${amount}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPopup;